import { useCurrentAccount } from 'hooks/admin/useAuth'
import { useSoldTicketsInfo } from 'hooks/admin/useSoldTicketsInfo'
import { useAuditoriumSeats, useDrag, useZoom } from 'hooks/user/useAuditoriumSeats'
import { Tooltip } from 'primereact/tooltip'
import { useEffect, useState } from 'react'
import { Ticket } from 'types/Event'
import { Seat, SeatStatus } from 'types/Location'
import { UserRole } from 'types/Organization'

import {
    Cell,
    ContainerSmall,
    Grid,
    GridContainerSmall,
    GridWrapperSmall,
    TitleBox,
    WrapperColumn,
    ZoomButton,
    ZoomContainer,
} from '../auditorium.style'
import { SeatColors } from './constants'
import { SeatIcon } from './seatIcon'

interface Props {
    rows: number | null
    columns: number | null
    seats: Seat[]
    tickets: Ticket[]
    middleColumn?: number
    onSelectedSeatsChange: (selectedSeats: string[]) => void
    eventId: string
    isGallery?: boolean
    galleryName?: string
}

export const AuditoriumSelling = ({
    rows,
    columns,
    seats,
    tickets,
    middleColumn,
    onSelectedSeatsChange,
    eventId,
    isGallery = false,
    galleryName = 'Karzat',
}: Props) => {
    const [loading, setLoading] = useState<boolean>(true)
    const { containerRef, handleDragStart, handleDragMove, handleDragEnd } = useDrag()
    const { zoomLevel, handleZoomIn, handleZoomOut } = useZoom(columns || 0)

    // Check if user is admin
    const { currentAccount } = useCurrentAccount()
    const isAdmin = currentAccount?.role === UserRole.superAdmin || currentAccount?.role === UserRole.orgAdmin

    // Fetch sold tickets info if user is admin
    const { data: soldTicketsInfo } = useSoldTicketsInfo(eventId, isAdmin)

    const { getTicketStatusOptimistic, getTicketPrice, generateSeatLabelData, handleSeatClick, selectedTickets } = useAuditoriumSeats(
        seats,
        onSelectedSeatsChange,
        tickets,
        eventId,
    )

    const getSeatColor = (seat: Seat | undefined, seatStatus: SeatStatus, isSelected: boolean): string => {
        if (!seat) return SeatColors[SeatStatus.NotAvailable]
        if (seatStatus === SeatStatus.Available && isSelected) return SeatColors[SeatStatus.Selected]
        return SeatColors[seatStatus]
    }

    useEffect(() => {
        setLoading(false)
    }, [rows, columns])

    useEffect(() => {
        if (containerRef.current && !loading) {
            const container = containerRef.current
            container.scrollLeft = container.scrollWidth / 2 - container.clientWidth / 2
        }
    }, [containerRef, loading, rows, columns])

    const isSeatClickable = (ticketStatus: SeatStatus): boolean => [SeatStatus.Selected, SeatStatus.Available].includes(ticketStatus)

    if (rows === null || columns === null) return null

    return (
        <ContainerSmall>
            <WrapperColumn>
                <TitleBox>Színpad</TitleBox>
                <GridContainerSmall
                    ref={containerRef}
                    onMouseDown={handleDragStart}
                    onMouseMove={handleDragMove}
                    onMouseUp={handleDragEnd}
                    onMouseLeave={handleDragEnd}
                >
                    <GridWrapperSmall
                        style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'center', transition: 'transform 0.3s ease' }}
                    >
                        {Array.from({ length: rows }).map((val, rowIndex) => (
                            <Grid rows={1} columns={columns} key={rowIndex}>
                                {Array.from({ length: columns }).map((value, columnIndex) => {
                                    const foundSeat = seats.find((s) => s.row === rowIndex && s.column === columnIndex)
                                    const ticketStatus = getTicketStatusOptimistic(rowIndex, columnIndex)
                                    const isSelected = foundSeat
                                        ? selectedTickets.some(
                                              (ticket: { seat_id: string | undefined }) => ticket.seat_id === foundSeat.seat_id,
                                          )
                                        : false
                                    const fillColor = getSeatColor(foundSeat, ticketStatus, isSelected)
                                    const isClickable = isSeatClickable(ticketStatus)
                                    const seatLabel = generateSeatLabelData(
                                        rowIndex,
                                        columnIndex,
                                        columns,
                                        seats,
                                        middleColumn,
                                        isGallery,
                                        galleryName,
                                    )?.label
                                    const ticketPrice = getTicketPrice(foundSeat?.seat_id)

                                    // Build tooltip content based on seat status and user role
                                    let tooltipContent = `${seatLabel}\n${ticketPrice} Ft`
                                    if (!isClickable && isAdmin && foundSeat?.seat_id && soldTicketsInfo?.[foundSeat.seat_id]) {
                                        const info = soldTicketsInfo[foundSeat.seat_id]
                                        tooltipContent = `${seatLabel}\n${ticketPrice} Ft\n\n🔒 Eladva:\n${info.customer_name}\n${info.email}\n${info.phone}`
                                    }

                                    return (
                                        <Cell
                                            id={`seat-${rowIndex}-${columnIndex}-${foundSeat?.seat_id}`}
                                            key={foundSeat?.seat_id || `${rowIndex}-${columnIndex}`}
                                            onClick={isClickable ? () => handleSeatClick(rowIndex, columnIndex) : undefined}
                                            style={{
                                                cursor: isClickable ? 'pointer' : 'not-allowed',
                                            }}
                                        >
                                            {foundSeat && (
                                                <Tooltip
                                                    target={`#seat-${rowIndex}-${columnIndex}-${foundSeat?.seat_id}`}
                                                    content={tooltipContent}
                                                    position="right"
                                                    mouseTrack
                                                    mouseTrackTop={10}
                                                />
                                            )}
                                            <div style={{ pointerEvents: 'none' }}>
                                                <SeatIcon fill={fillColor} />
                                            </div>
                                        </Cell>
                                    )
                                })}
                            </Grid>
                        ))}
                    </GridWrapperSmall>
                </GridContainerSmall>
                <ZoomContainer>
                    <ZoomButton type="button" onClick={handleZoomIn} icon="pi pi-search-plus" />
                    <ZoomButton type="button" onClick={handleZoomOut} icon="pi pi-search-minus" />
                </ZoomContainer>
            </WrapperColumn>
        </ContainerSmall>
    )
}
